import type { FetchError } from 'ofetch';

export const useRolesStore = defineStore('roles', () => {
  const { getRole: getRoleFromApi, getAllRoles } = useApi();
  const permissionsStore = usePermissionsStore();
  const { permissions } = storeToRefs(permissionsStore);

  const roles = ref<Role[]>([]);
  const rolesLoading = ref(false);
  /**
   * A list of unique permission ids filtered down from the permissions
   * associated with roles. Used to filter down the list of all permissions
   * to the ones that can be assigned via roles.
   */
  const permissionIdsFromRoles = computed(() => {
    return roles.value.reduce(
      (acc, role) => [...acc, ...role.permissions.map(permission => permission.id)],
      [] as number[],
    );
  });
  /**
   * A hydrated version of `permissionIdsFromRoles`. A list of Permission objects
   * rather than ids.
   */
  const permissionsFromRoles = computed(() => {
    return permissions.value
      .filter(permission => permissionIdsFromRoles.value.includes(permission.id));
  });

  /**
   * Fetches all of the roles from the server and populates the store.
   * If the `forceRefresh` argument is set to true, then the data is always
   * re-requested. Otherwise, the last retrieved value will be returned if
   * one exists.
   *
   * @param forceRefresh
   */
  const fetchRoles = async (forceRefresh = false) => {
    if (!forceRefresh && roles.value.length > 0) {
      return;
    }
    rolesLoading.value = true;

    try {
      // Request permissions whenever we request roles, because of the computed properties
      const [_, _roles] = await Promise.all([
        permissionsStore.fetchPermissions(forceRefresh),
        getAllRoles(),
      ]);
      roles.value = _roles.results;
    }
    catch (err) {
      console.error('Error fetching roles:', (err as FetchError).message);
    }
    rolesLoading.value = false;
  };

  /**
   * Retrieves a specific role by id. If the list of roles has already been
   * retrieved, a role with a matching id is searched for there first.
   * Otherwise, the role is requested from the server.
   *
   * @param roleId
   */
  const getRole = async (roleId: number) => {
    if (roles.value.length > 0) {
      const role: Role | undefined = roles.value.find(role => role.id === roleId);
      if (role) {
        return role;
      }
    }
    try {
      return await getRoleFromApi(roleId);
    }
    catch (err) {
      console.log(`Error getting role ${roleId}:`, (err as FetchError).message);
      return null;
    }
  };

  return {
    roles,
    rolesLoading,
    permissionIdsFromRoles,
    permissionsFromRoles,
    fetchRoles,
    getRole,
  };
});
